import { AxiosError } from 'axios';
import type { JSX } from 'react';
import { useContext, useEffect, useMemo } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';

import PersonAvailabilityChannel from '@/channels/PersonAvailabilityChannel';
import PersonMessagesChannel from '@/channels/PersonMessagesChannel';
import PersonSidebarNav from '@/components/PersonSidebarNav';
import PersonToolbar from '@/components/PersonToolbar';
import AppContext from '@/contexts/appContext';
import { useEncounterById } from '@/hooks/useEncounters';
import usePerson from '@/hooks/usePerson';
import PersonContext from '@/pages/Person/PersonContext';
import Alert from '@/ui/components/Alert';
import Heading from '@/ui/components/Heading';
import PersonHeader from '@/ui/components/PersonHeader';
import Spinner from '@/ui/components/Spinner';
import encounterNavItem from '@/utils/encounterNavItem';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';
import { ENCOUNTER_VIEW_BETA_TESTERS_GROUP, isInGroup } from '@/utils/userPermission';

const EncounterDetails = (): JSX.Element | null => {
  const { addMessage } = useContext(AppContext);
  const { encounterId, encounterType } = useParams();
  const { permissions } = useContext(AppContext);

  // Gated until the encounter view's member info is ready to launch.
  const showNewCounselorView = isInGroup(permissions, ENCOUNTER_VIEW_BETA_TESTERS_GROUP);
  const loc = useLocation();

  const state = loc.state;
  const { path, person: personFromState } = state || {};

  const {
    data: encounter,
    error: errorEncounter,
    isError: isErrorEncounter,
    isLoading: isLoadingEncounter,
    refetch: refetchEncounter
  } = useEncounterById({
    encounterId,
    encounterType
  });

  const person = personFromState || encounter?.person;

  // The rail lists the person's pages; without this the open encounter is nowhere in it.
  const openRecord = useMemo(
    () =>
      encounterType && encounterId ? encounterNavItem({ encounterType, encounterId }) : undefined,
    [encounterId, encounterType]
  );

  useEffect(() => {
    if (isErrorEncounter) {
      addMessage(mapAxiosErrorMessages(errorEncounter as AxiosError));
    }
  }, [addMessage, errorEncounter, isErrorEncounter]);

  const {
    handleUpdatePerson,
    usePersonQuery: { isLoading: isLoadingPerson, data: personResponse, refetch }
  } = usePerson({
    id: person?.id,
    enabled: !!person?.id
  });

  if (isLoadingEncounter || isLoadingPerson)
    return (
      <div className="flex justify-center mt-4">
        <Spinner />
      </div>
    );

  if (!encounter)
    return (
      <div className="flex justify-center mt-4">
        <Heading
          className="truncate mb-4"
          text="Failed to load encounter details"
          title="Failed to load encounter details"
          value={3}
        />
      </div>
    );

  const deletedAlert = encounter.deleted_at && (
    <Alert level="error" message="This encounter has been deleted." dismissable={false} />
  );

  if (!personResponse?.data)
    return (
      <>
        <div className="mx-auto w-full max-w-7xl px-2 sm:px-4 lg:px-6">{deletedAlert}</div>
        <Outlet context={{ encounter, path }} />
      </>
    );

  return (
    <PersonContext.Provider
      value={{ person: personResponse.data, refetch, updatePerson: handleUpdatePerson }}>
      <PersonSidebarNav openRecord={openRecord} />
      <PersonHeader person={personResponse.data} />
      <PersonToolbar
        hideCardOrderMenu={!showNewCounselorView}
        navDefaultOptionLabel="Encounter"
        person={personResponse.data}
      />
      <div className="mx-auto w-full max-w-7xl px-2 sm:px-4 lg:px-6">{deletedAlert}</div>
      {/*
        Person routes wrap their Outlet in PersonChannels; this view opens only the
        channels its blocks currently use. Add others here as blocks come to need them,
        or the PersonChannels component if it's decided we need them all.
      */}
      <PersonAvailabilityChannel personId={person.id}>
        <PersonMessagesChannel personId={person.id}>
          <Outlet context={{ encounter, path, refetchEncounter }} />
        </PersonMessagesChannel>
      </PersonAvailabilityChannel>
    </PersonContext.Provider>
  );
};

export default EncounterDetails;
